import { type IconName } from '#app/components/ui/icon'
import { NavigationRoutes } from '#app/config'
import { path } from '#app/utils/pz/routes'
import { canSeeNavItemWithContext } from './access'

/**
 * Navigation item resolved for runtime use in UI components.
 */
export type NavigationItem = {
  id: number
  /** Translated label */
  name: string
  /** Href that the link should navigate to */
  href: string
  /** Optional icon name */
  icon: IconName
  /** Whether the item represents a dialog form */
  form?: boolean
  /** Optional React component name if form */
  component?: string
  /** Target attribute for external links */
  target?: string
  /** Navigation group for sidebar organization */
  group?: string
}

export type NavigationContext = {
  featureFlags: unknown
  user: unknown
}

// Internal: resolve a leaf from a NavigationPaths-like tree by routeKey
function getLeafFromPaths(paths: any, routeKey: string): any | null {
  if (!paths || !routeKey) return null
  const parts = routeKey.split('.')
  let cur: any = paths
  for (const p of parts) {
    if (!cur || typeof cur !== 'object' || !(p in cur)) return null
    cur = cur[p]
  }
  if (cur && typeof cur === 'object' && 'path' in cur) return cur
  return null
}

/**
 * Resolves the href for a raw navigation config entry.
 * If an explicit `href` is provided it is returned as-is.
 * Otherwise, the `routeKey` is converted to a path via the generated `path` helper.
 */
function resolveHref(item: any, overridePaths?: any): string {
  if ('href' in item && typeof item.href === 'string') {
    return item.href
  }
  if ('routeKey' in item && typeof item.routeKey === 'string') {
    try {
      if (overridePaths) {
        const leaf = getLeafFromPaths(overridePaths, item.routeKey as string)
        if (leaf?.path && typeof leaf.path === 'string') return leaf.path
      }
      const p = path(item.routeKey as any)
      return (p as unknown as string) || '#'
    } catch {
      return '#'
    }
  }
  // Fallback to root
  return '#'
}

/**
 * Maps a list of raw navigation items into UI-ready `NavigationItem`s.
 */
function mapItems(
  list: any[],
  t: (key: any, ...args: any[]) => any,
  overridePaths?: any,
): NavigationItem[] {
  return list.map(item => ({
    id: item.id,
    name: t(item.translationKey),
    href: resolveHref(item, overridePaths),
    icon: item.icon as IconName,
    form: item.type === 'dialog',
    component: (item as any).component ?? '',
    target: (item as any).target,
    group: (item as any).group,
  }))
}

/**
 * Returns the three navigation sections already mapped and translated using aggregated navigation.routes only.
 */
export type NavigationGroups = 'entities' | 'support' | 'user' | 'marketing' | 'tools' | 'social' | 'landings' | 'creationAssistant' | 'studios'
export type NavigationArea = 'admin' | 'dashboard' | 'user' | 'public'
export type GroupedNavigation = Partial<Record<NavigationGroups, NavigationItem[]>>

export type NavigationFilter = Partial<{
  type: 'link' | 'dialog' | 'view'
  entityType: string
  routeKey: string
  enabled: boolean
}>

type GetNavigationBaseOptions = {
  area: NavigationArea
  groups?: NavigationGroups[]
  filter?: NavigationFilter
}

type GetNavigationGroupedOptions = GetNavigationBaseOptions & {
  byGroup: true
}

type GetNavigationFlatOptions = GetNavigationBaseOptions & {
  byGroup?: false
}

/**
 * Unified navigation resolver. Applies feature-flag, role, permission, and auth gating
 * for all areas. Does NOT use an `enabled` flag from aggregated navigation routes;
 * menu visibility is controlled by roles/feature flags and optionally by canonical
 * NavigationPaths (overridePaths). Supports filtering and grouping.
 */
export function getNavigation(
  t: (key: any, ...args: any[]) => any,
  ctx: NavigationContext,
  opts: GetNavigationGroupedOptions,
  overrideRoutes?: any[],
  overridePaths?: any,
): GroupedNavigation
export function getNavigation(
  t: (key: any, ...args: any[]) => any,
  ctx: NavigationContext,
  opts: GetNavigationFlatOptions,
  overrideRoutes?: any[],
  overridePaths?: any,
): NavigationItem[]
export function getNavigation(
  t: (key: any, ...args: any[]) => any,
  ctx: NavigationContext,
  opts: GetNavigationBaseOptions & { byGroup?: boolean },
  overrideRoutes?: any[],
  overridePaths?: any,
): NavigationItem[] | GroupedNavigation {
  const { area, groups, filter, byGroup } = opts
  // Allow callers (SSR routes) to provide DB-first navigation config
  // so that client-rendered sidebars honor Config Studio changes.
  const aggregatedAll = (overrideRoutes as any[]) || (NavigationRoutes as any[]) || []
  let items = aggregatedAll.filter((i) => i.area === area)

  // Apply central gating (feature flags, roles, permissions, auth)
  items = items.filter((i) => canSeeNavItemWithContext(i, ctx))

  // Filter out items with enabled: false directly on the navigation route
  items = items.filter((i) => i.enabled !== false)

  // If routeKey is present and overridePaths provided, filter out items whose route leaf is disabled
  if (overridePaths) {
    items = items.filter((i) => {
      if (!i?.routeKey || typeof i.routeKey !== 'string') return true
      const leaf = getLeafFromPaths(overridePaths, i.routeKey)
      if (!leaf) return true
      const effEnabled = typeof leaf.enabled === 'boolean' ? leaf.enabled : true
      return effEnabled
    })
  }

  // Optional filter
  if (filter) {
    items = items.filter((i) => {
      if (filter.type && i.type !== filter.type) return false
      if (filter.entityType && i.entityType !== filter.entityType) return false
      if (filter.routeKey && i.routeKey !== filter.routeKey) return false
      if (typeof filter.enabled === 'boolean') {
        const eff = typeof i.enabled === 'boolean' ? i.enabled : true
        if (eff !== filter.enabled) return false
      }
      return true
    })
  }

  // If byGroup, split by requested groups only
  if (byGroup) {
    const groupSet = new Set<NavigationGroups>(groups || ['entities', 'support', 'user'])
    const grouped: Record<NavigationGroups, any[]> = {
      entities: items.filter((i) => i.group === 'entities'),
      support: items.filter((i) => i.group === 'support'),
      user: items.filter((i) => i.group === 'user'),
      marketing: items.filter((i) => i.group === 'marketing'),
      tools: items.filter((i) => i.group === 'tools'),
      social: items.filter((i) => i.group === 'social'),
      landings: items.filter((i) => i.group === 'landings'),
      creationAssistant: items.filter((i) => i.group === 'creationAssistant'),
      studios: items.filter((i) => i.group === 'studios'),
    }
    const result: GroupedNavigation = {}
    for (const g of groupSet) {
      result[g] = mapItems(grouped[g], t, overridePaths)
    }
    return result
  }

  // Flat list
  return mapItems(items, t, overridePaths)
}

// Backward-compatibility wrappers
export function getAdminNavigation(
  t: any,
  _prefs: any,
  ctx: { featureFlags: any; user: any },
) {
  return getNavigation(t, ctx, { area: 'admin' }) as NavigationItem[]
}

export function getSidebarNavigation(
  t: any,
  _prefs: any,
  area: 'user' | 'dashboard' = 'user',
) {
  // Note: old impl used prefs to hide by app-sidebar. That mechanism is deprecated.
  // New resolver uses flags/roles/permissions and `enabled` gating from navigation routes.
  return getNavigation(t, { featureFlags: {}, user: undefined }, { area, byGroup: true }) as any
}

